// ===================================================
// TariffContext — Адаптер для markbase.ru
// Источник истины: markbaseCORE/modules/tariffs/frontend/src/contexts/TariffContext.jsx
// Этот файл — адаптер: подключает данные из billing API
// для использования в компонентах markbase.ru
// ===================================================
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { devLogApiError } from '../utils/devLogApiError';
import { billingApiPath } from '../utils/billingApiPaths';

const TariffContext = createContext();

/**
 * Провайдер контекста тарифов для markbase.ru.
 * Все данные загружаются из billing API (который читает из uam.tariffs).
 */
export const TariffProvider = ({ children, axios }) => {
  const [currentTariff, setCurrentTariff] = useState(null);
  const [availableTariffs, setAvailableTariffs] = useState([]);
  const [effectiveLimits, setEffectiveLimits] = useState(null);
  const [servicePricing, setServicePricing] = useState([]);
  const [customPricing, setCustomPricing] = useState([]);
  const [usage, setUsage] = useState({});
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  // Extended billing state (Migration 115)
  const [resourceCounts, setResourceCounts] = useState(null);   // live usage from /api/tariffs/v1/usage
  const [spendLimits, setSpendLimits] = useState([]);           // company_module_spend_limits
  const [billingEvents, setBillingEvents] = useState([]);       // billing_events (audit)
  const [billingMode, setBillingModeState] = useState(null);    // billing_mode + block_on_zero
  const [usageLoading, setUsageLoading] = useState(false);

  const [error, setError] = useState(null);
  const [dataScope, setDataScope] = useState(null);
  const scope = useSelector((state) => {
    const auth = state.auth;
    return auth?.isAuthenticated && auth.bootstrapStatus === 'ready' && auth.user?.id && auth.company?.id
      ? String(auth.user.id) + ':' + String(auth.company.id) : null;
  });
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const lastUpdateRef = useRef({ scope: null, timestamp: null });
  const requestRef = useRef(0);
  const extendedRequestRef = useRef(0);

  const loadTariffData = useCallback(async (force = false) => {
    const requestScope = scopeRef.current;
    if (!axios || !requestScope) return;
    if (!force && lastUpdateRef.current.scope === requestScope && lastUpdateRef.current.timestamp) return;
    const requestId = ++requestRef.current;
    const isCurrent = () => scopeRef.current === requestScope && requestRef.current === requestId;
    setLoading(true);
    setError(null);
    try {
      const config = { errorPresentation: 'inline', cacheTtlMs: 0, noDedup: true };
      const [companyResult, availableResult] = await Promise.allSettled([
        axios.get(billingApiPath('company-billing-info'), config),
        axios.get(billingApiPath('available-tariffs'), config),
      ]);
      if (!isCurrent()) return;
      setDataScope(requestScope);
      const companyInfo = companyResult.status === 'fulfilled' ? companyResult.value?.data : null;
      const available = availableResult.status === 'fulfilled' ? availableResult.value?.data : null;
      if (available?.success === true) {
        const payload = available.data;
        setAvailableTariffs(Array.isArray(payload) ? payload : (Array.isArray(payload?.tariffs) ? payload.tariffs : []));
      }
      if (companyInfo?.success !== true || !companyInfo.data) {
        setError('Не удалось загрузить тариф компании. Повторите попытку.');
        return;
      }
      const data = companyInfo.data;
      const limits = data.limits || {};
      setCurrentTariff(data.tariff || null);
      setEffectiveLimits({
        tariff_limits: limits.tariff_limits || {},
        custom_limits: limits.custom_limits || {},
        effective_limits: limits.effective_limits || {},
        limits: limits.effective_limits || limits.tariff_limits || {},
        is_unlimited: limits.is_unlimited || data.tariff?.is_unlimited || false,
      });
      setServicePricing(Array.isArray(data.servicePricing) ? data.servicePricing : []);
      setCustomPricing(Array.isArray(data.customPricing) ? data.customPricing : []);
      setUsage(data.usage || {});
      const timestamp = Date.now();
      lastUpdateRef.current = { scope: requestScope, timestamp };
      setLastUpdate(timestamp);
      if (available?.success !== true) setError('Не удалось обновить список тарифов. Повторите попытку.');
    } catch (failure) {
      if (isCurrent()) {
        setError('Не удалось загрузить тариф компании. Повторите попытку.');
        devLogApiError('контекст тарифов: загрузка', failure);
      }
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [axios]);

  const refresh = useCallback(() => loadTariffData(true), [loadTariffData]);

  const loadExtendedBillingData = useCallback(async () => {
    const requestScope = scopeRef.current;
    if (!axios || !requestScope) return;
    const requestId = ++extendedRequestRef.current;
    const isCurrent = () => scopeRef.current === requestScope && extendedRequestRef.current === requestId;
    setUsageLoading(true);
    try {
      const config = { errorPresentation: 'inline', cacheTtlMs: 0, noDedup: true };
      const [usageRes, limitsRes, eventsRes, modeRes] = await Promise.all([
        axios.get('/api/tariffs/v1/usage', config).catch(() => null),
        axios.get('/api/tariffs/v1/spend-limits', config).catch(() => null),
        axios.get('/api/tariffs/v1/events?limit=20', config).catch(() => null),
        axios.get('/api/tariffs/v1/billing-mode', config).catch(() => null),
      ]);
      if (!isCurrent()) return;
      setDataScope(requestScope);
      if (usageRes?.data?.usage) setResourceCounts(usageRes.data.usage);
      if (limitsRes?.data?.spend_limits) setSpendLimits(limitsRes.data.spend_limits);
      if (eventsRes?.data?.events) setBillingEvents(eventsRes.data.events);
      if (modeRes?.data?.billing_mode) setBillingModeState(modeRes.data);
    } finally {
      if (isCurrent()) setUsageLoading(false);
    }
  }, [axios]);

  const getEffectiveLimit = useCallback((resourceType) => {
    if (!resourceType || !effectiveLimits) {
      return { effective: 0, tariff: 0, custom: null, is_custom: false };
    }
    // Безлимитный тариф
    if (effectiveLimits?.is_unlimited) {
      return { effective: null, tariff: null, custom: null, is_custom: false, is_unlimited: true };
    }

    const custom = effectiveLimits?.custom_limits?.[resourceType];
    const tariff = effectiveLimits?.tariff_limits?.[resourceType];
    const effective = effectiveLimits?.effective_limits?.[resourceType];

    return {
      effective: effective !== null && effective !== undefined ? Number(effective) : 0,
      tariff: tariff !== null && tariff !== undefined ? Number(tariff) : 0,
      custom: custom ?? null,
      is_custom: custom !== undefined && custom !== null,
      is_unlimited: effective === null,
    };
  }, [effectiveLimits]);

  const getEffectivePrice = useCallback((resourceType, currentUsage) => {
    const usageIndex = Number.isFinite(Number(currentUsage)) ? Number(currentUsage) : 0;

    // Индивидуальная цена
    const custom = customPricing.find(
      (cp) =>
        cp?.resource_type === resourceType &&
        usageIndex >= Number(cp?.min_units ?? 0) &&
        (cp?.max_units === null || cp?.max_units === undefined || usageIndex <= Number(cp.max_units))
    );

    if (custom) {
      return {
        price_per_day: Number(custom?.custom_price_per_day ?? 0) || 0,
        price_per_month: Number(custom?.custom_price_per_month ?? 0) || 0,
        is_custom: true,
        tier_name: custom?.tier_name || 'Индивидуальная цена',
        free_tier: false
      };
    }

    // Глобальная цена
    const tier = servicePricing
      .filter(
        (sp) =>
          sp?.resource_type === resourceType &&
          usageIndex >= Number(sp?.min_units ?? 0) &&
          (sp?.max_units === null || sp?.max_units === undefined || usageIndex <= Number(sp.max_units))
      )
      .sort((a, b) => Number(a?.display_order ?? 0) - Number(b?.display_order ?? 0))[0];

    if (tier) {
      return {
        price_per_day: Number(tier?.price_per_day ?? 0) || 0,
        price_per_month: Number(tier?.price_per_month ?? 0) || 0,
        is_custom: false,
        tier_name: tier?.tier_name || 'Стандартная цена',
        free_tier: Boolean(tier?.free_tier)
      };
    }

    return { price_per_day: 0, price_per_month: 0, is_custom: false, tier_name: 'Не найдено', free_tier: false };
  }, [customPricing, servicePricing]);

  useEffect(() => {
    scopeRef.current = scope;
    requestRef.current += 1;
    extendedRequestRef.current += 1;
    lastUpdateRef.current = { scope, timestamp: null };
    setDataScope(scope);
    setCurrentTariff(null);
    setAvailableTariffs([]);
    setEffectiveLimits(null);
    setServicePricing([]);
    setCustomPricing([]);
    setUsage({});
    setLastUpdate(null);
    setResourceCounts(null);
    setSpendLimits([]);
    setBillingEvents([]);
    setBillingModeState(null);
    setLoading(false);
    setUsageLoading(false);
    setError(null);
    if (scope) loadTariffData();
  }, [scope, loadTariffData]);

  useEffect(() => () => {
    scopeRef.current = null;
    requestRef.current += 1;
    extendedRequestRef.current += 1;
  }, []);

  const current = Boolean(scope && dataScope === scope);
  const value = {
    error: current ? error : null,
    currentTariff: current ? currentTariff : null,
    availableTariffs: current ? availableTariffs : [],
    effectiveLimits: current ? effectiveLimits : null,
    servicePricing: current ? servicePricing : [],
    customPricing: current ? customPricing : [],
    usage: current ? usage : {},
    loading: current ? loading : false,
    lastUpdate: current ? lastUpdate : null,
    refresh,
    loadTariffData,
    loadBillingInfo: refresh,
    getEffectiveLimit,
    getEffectivePrice,
    // Extended billing (migration 115)
    resourceCounts: current ? resourceCounts : null,
    spendLimits: current ? spendLimits : [],
    billingEvents: current ? billingEvents : [],
    billingMode: current ? billingMode : null,
    usageLoading: current ? usageLoading : false,
    loadExtendedBillingData,
  };

  return (
    <TariffContext.Provider value={value}>
      {children}
    </TariffContext.Provider>
  );
};

export const useTariff = () => {
  const context = useContext(TariffContext);
  if (!context) {
    throw new Error('useTariff используйте только внутри TariffProvider');
  }
  return context;
};
